import { generatePath } from 'react-router-dom';

export const HATCH_URL_BASE = '/next';

export type BuildUrlOptions = {
  query?: Record<string, string>;
  hash?: string;
};

type ParamsDict<P extends string[]> = {
  [K in P[number]]: string;
};

export class HatchRoute<P extends string[]> {
  constructor(
    public rawRouteTemplate: string,
    public paramsList: P,
  ) {}

  build(
    // Conditional types to handle optional parameters in the case of a route with no params, nice-to-have only
    ...args: [] extends P
      ? [params?: ParamsDict<P>, options?: BuildUrlOptions]
      : [params: ParamsDict<P>, options?: BuildUrlOptions]
  ): string {
    const [params, options] = args;

    // Only way of having no params is if P is empty, meaning a empty object should suffice as params
    // The cast is justified by the fact that we know P is empty
    return this._build(params ?? ({} as ParamsDict<P>), options);
  }

  private _build(
    params: ParamsDict<P>,
    { query, hash }: BuildUrlOptions = {},
  ): string {
    const url = new URL(
      generatePath(`${HATCH_URL_BASE}/${this.rawRouteTemplate}`, params),
      location.href,
    );

    if (query) {
      for (const [key, value] of Object.entries(query)) {
        url.searchParams.append(key, value);
      }
    }

    if (hash) {
      url.hash = hash;
    }

    return url.href.substring(location.origin.length);
  }
}

export const hatchRoute = <const P extends string[]>(
  routePath: TemplateStringsArray,
  ...paramsList: P
): HatchRoute<P> => {
  const route = routePath.reduce((acc, curr, i) => {
    const param = paramsList[i] ? `:${paramsList[i]}` : '';
    return acc + curr + param;
  }, '');

  return new HatchRoute(route, paramsList);
};

// Library detail URL slugs use hyphens instead of underscores so links posted
// to Slack (and elsewhere) don't render with `_word_` italic auto-formatting.
// Mirrors the transform in stak/slack-app deep_link generation.
export const artifactTypeToSlug = (artifactType: string): string =>
  artifactType.replace(/_/g, '-');

/**
 * Hatch routes and parameters
 */
export const HatchRoutes = {
  ACCOUNT: hatchRoute`/account`,
  ALL: hatchRoute`*`,
  ANALYTICS: hatchRoute`/analytics`,
  ANALYTICS_ENGAGEMENT: hatchRoute`/analytics/engagement`,
  ANALYTICS_REVENUE: hatchRoute`/analytics/revenue`,
  ANSWER_DETAIL: hatchRoute`/answers/${'questionId'}`,
  /** Legacy full Smart Answers list (coverage/grouping debug). */
  ANSWERS_LIST: hatchRoute`/answers/list`,
  ANSWERS: hatchRoute`/answers`,
  CHAT_SESSION: hatchRoute`/chat/${'sessionId'}`,
  CHAT_SHARE: hatchRoute`/chat/share/${'sharedThreadId'}`,
  // Bare namespace root — redirects to the Competitors tab (see router), so
  // a truncated detail URL never dead-ends on a 404.
  COMPETITORS: hatchRoute`/competitors`,
  // Competitor/product entity page. The param is named `entityId` so the
  // existing ActiveEntityProvider (which reads params.entityId) resolves the
  // graph node with no extra wiring. Works for both Company and Product nodes —
  // they share the EntityGraphNode.id field.
  COMPETITOR_DETAIL: hatchRoute`/competitors/${'entityId'}`,
  COMPETITOR_INSIGHT_DETAIL: hatchRoute`/insights/card/${'id'}`,
  DEAL_TIP_DETAIL: hatchRoute`/deal-tips/${'id'}`,
  HOME: hatchRoute`/`,
  // The Launchpad's own route. The index route hands `/next` to the onboarding
  // checklist while an admin still has setup steps left, so "New chat" in the
  // nav needs a path the takeover doesn't own (see the router).
  NEW_CHAT: hatchRoute`/new`,
  INBOX: hatchRoute`/inbox`,
  LIBRARY: hatchRoute`/library`,
  LIBRARY_DETAIL: hatchRoute`/library/${'artifactType'}/${'artifactId'}`,
  FINISH_SETUP: hatchRoute`/finish-setup`,
  // The checklist's step 1 destination — "Your competitive map" wraps the
  // Settings → Competitors content in the checklist's own framing, so it is a
  // route of its own rather than a redirect into settings.
  FINISH_SETUP_LANDSCAPE: hatchRoute`/finish-setup/landscape`,
  // The checklist's step 2 destination — reviewing the assigned answers is a
  // screen of its own, not an inline expansion of the step card.
  FINISH_SETUP_ANSWERS: hatchRoute`/finish-setup/answers`,
  // Coach Mode for one assigned answer, linkable in its own right.
  FINISH_SETUP_ANSWER_COACH: hatchRoute`/finish-setup/answers/${'questionId'}`,
  // Module pages. The `/m/` prefix keeps the `:moduleSlug` segment from
  // colliding with the named top-level hatch routes. All consumers go through
  // .build(), but shared URLs make the shape a compatibility commitment:
  // changing it needs redirects for the old links.
  MODULE: hatchRoute`/m/${'moduleSlug'}`,
  MODULE_TAB: hatchRoute`/m/${'moduleSlug'}/${'tabSlug'}`,
  // A tab's detail view, rendered by ModulePage inside the module chrome so the
  // tab strip stays put (e.g. a campaign opened from Win Loss → Home,
  // `…/home/:campaignId`).
  // ModulePage ignores `detailId` entirely — resolving the active tab from
  // `tabSlug` is unchanged; it's the tab's own component that reads the param
  // and swaps its list for a detail view.
  MODULE_TAB_DETAIL: hatchRoute`/m/${'moduleSlug'}/${'tabSlug'}/${'detailId'}`,
  // Same idea one level deeper, for a tab hosting more than one kind of detail
  // (Win Loss → Settings holds both opportunity rules and contact rules, so the
  // id alone wouldn't say which to load: `…/settings/rules/:ruleId`). A tab may
  // read the segments its own way — Win Loss → Home puts the campaign id first
  // and the view second (`…/home/:campaignId/settings`). The tab owns that
  // mapping; build Win Loss URLs through `libs/workspace/components/winLossUrls`.
  MODULE_TAB_SUB_DETAIL: hatchRoute`/m/${'moduleSlug'}/${'tabSlug'}/${'detailKind'}/${'detailId'}`,
  // An action on one of those details: editing a rule
  // (`…/settings/rules/:ruleId/edit`) or one section of a campaign's settings
  // (`…/home/:campaignId/settings/research-goals`).
  MODULE_TAB_SUB_DETAIL_ACTION: hatchRoute`/m/${'moduleSlug'}/${'tabSlug'}/${'detailKind'}/${'detailId'}/${'detailAction'}`,
  PAIRWISE_REVIEWS: hatchRoute`/pairwise-reviews`,
  PAIRWISE_REVIEWS_EVALUATE: hatchRoute`/pairwise-reviews/${'id'}`,
  SETTINGS: hatchRoute`/settings/*`,
  SETTINGS_COMPETITORS: hatchRoute`/settings/competitors`,
  SETTINGS_DEAL_TIPS: hatchRoute`/settings/deal-tips`,
  SETTINGS_EXPERIMENTS: hatchRoute`/settings/experiments`,
  SETTINGS_EXPERIMENT_NEW: hatchRoute`/settings/experiments/new`,
  SETTINGS_EXPERIMENT_EDIT: hatchRoute`/settings/experiments/${'id'}/edit`,
  SETTINGS_FILES: hatchRoute`/settings/files`,
  SETTINGS_BATTLECARD_INSTRUCTIONS: hatchRoute`/settings/battlecard-instructions`,
  SETTINGS_CHAT_INSTRUCTIONS: hatchRoute`/settings/chat-customization`,
  SETTINGS_INTEGRATIONS: hatchRoute`/settings/integrations`,
  SETTINGS_INTEGRATIONS_SETTINGS: hatchRoute`/settings/integrations/${'slug'}/${'path'}`,
  // OAuth callback for data-sync integrations. Needs its own route because the
  // callback tail (`auth/complete`) is two path segments and therefore never
  // matches SETTINGS_INTEGRATIONS_SETTINGS's single-segment `:path` param —
  // without this, the callback falls through to the `settings/*` splat and the
  // AuthComplete handshake never runs. See buildAuthUrlForHatch.
  SETTINGS_INTEGRATIONS_AUTH_COMPLETE: hatchRoute`/settings/integrations/${'slug'}/auth/complete`,
  SETTINGS_INTEGRATIONS_MODALS: hatchRoute`/settings/integrations/${'slug'}`,
  SETTINGS_SNIPPETS: hatchRoute`/settings/snippets`,
  SETTINGS_SOURCES: hatchRoute`/settings/sources`,
  SETTINGS_SSO: hatchRoute`/settings/sso`,
  SETTINGS_USERS: hatchRoute`/settings/users`,
  SETTINGS_USERS_DETAIL: hatchRoute`/users/${'id'}`,
  SNIPPET_EDIT: hatchRoute`/snippets/${'id'}`,
  SNIPPET_NEW: hatchRoute`/snippets/new`,
  UNAUTHORIZED: hatchRoute`/not-authorized`,
  TASK_DETAIL: hatchRoute`/tasks/${'taskId'}`,
  // Content updates inbox (ENG-3548). Named after the backend's /v0/updates;
  // the product name is still TBD, so the path is kept generic.
  UPDATES_INBOX: hatchRoute`/updates`,
  UPDATES_INBOX_ITEM: hatchRoute`/updates/${'updateId'}`,
  // Review settings (ENG-3542), a sub-view of the inbox as in the design.
  UPDATES_REVIEW_SETTINGS: hatchRoute`/updates/settings`,
  WIN_LOSS: hatchRoute`/win-loss/`,
} as const satisfies Record<string, HatchRoute<any>>;

/**
 * True for either path the Launchpad renders on: the Hatch index route and
 * NEW_CHAT. The nav's "New chat" entry links to NEW_CHAT but has to highlight
 * on both, since a user who is not mid-onboarding still lands on the index.
 */
export const isLaunchpadPath = (pathname: string): boolean => {
  // Trailing slash stripped without a regex: the repeat-quantifier form trips
  // the ReDoS lint rule, and one slash is all react-router ever hands over.
  const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  return (
    normalized === HATCH_URL_BASE ||
    normalized === `${HATCH_URL_BASE}${HatchRoutes.NEW_CHAT.rawRouteTemplate}`
  );
};

export type ChatSessionLocationStateType =
  | {
      isNewChat?: boolean;
    }
  | undefined;
